import React, { useState, useEffect } from 'react';
import crossIcon from '../../assets/cross.svg';
import CustomCheckbox from './CustomCheckbox';
import apiHandler from '../../services/apiServices';
import { toast } from 'react-toastify';
import { AnimatePresence, motion } from 'framer-motion';

interface FeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  onSubmitted: () => void;
}

const feedbackOptions = [
  'Incorrect contact information',
  'Lead is not relevant to my needs',
  'Duplicate lead',
  'Lacks Sufficient Detail',
  'Other'
];

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ isOpen, onClose, leadId, onSubmitted }) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOptions([]);
      setOtherReason('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOptionToggle = (option: string) => {
    setSelectedOptions(prev => 
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const feedbacks = selectedOptions.includes('Other') 
        ? [...selectedOptions.filter(opt => opt !== 'Other'), otherReason]
        : selectedOptions;

      await apiHandler.patch(`/intent-leads/${leadId}`, {
        feedbacks: feedbacks
      });

      onSubmitted();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    try {
      await apiHandler.patch(`/intent-leads/${leadId}`, {
        feedbacks: []
      });
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[200]"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div 
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
            className="flex flex-col w-[400px] bg-[#FFFFFF] rounded-[16px] shadow-lg p-4 gap-4"
            onClick={e => e.stopPropagation()}
          >
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex justify-between items-center"
            >
              <h2 className="text-[16px] font-medium text-[#222222]">
                Help us improve your leads:
              </h2>
              <button 
                onClick={onClose}
                className="text-[#75777C] hover:text-[#222222] transition-colors"
              >
                <img src={crossIcon} alt="close" className="w-3 h-3" />
              </button>
            </motion.div>

            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col"
            >
              {/* Options */}
              <div className="flex flex-col">
                <div className="flex flex-col gap-3">
                  {feedbackOptions.map((option) => (
                    <div 
                      key={option}
                      onClick={() => handleOptionToggle(option)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <CustomCheckbox
                        checked={selectedOptions.includes(option)}
                        onChange={() => handleOptionToggle(option)}
                        className="flex-shrink-0"
                      />
                      <span className="text-[14px] text-[#222222] font-normal select-none">
                        {option}
                      </span>
                    </div>
                  ))}
                  
                  {/* Other Reason Input Field */}
                  {selectedOptions.includes('Other') && (
                    <input
                      type="text"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="Let's know your reason"
                      className="px-3 py-2 text-sm text-[#222222] rounded-lg border border-[#DDDDDD] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] placeholder:text-[#9FA1A7]"
                    />
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-[14px] text-[#222222] border border-[#DDDDDD] hover:bg-[#EEEEEE] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={selectedOptions.length === 0 || (selectedOptions.includes('Other') && !otherReason) || isSubmitting}
                  className={`px-4 py-2 rounded-lg text-[14px] transition-all transform active:scale-95 ${
                    selectedOptions.length === 0 || (selectedOptions.includes('Other') && !otherReason) || isSubmitting
                      ? 'bg-[#EEEEEE] text-[#75777C] cursor-not-allowed'
                      : 'bg-[#29DC5B] text-[#222222] hover:bg-[#22c55e]'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-[#EEEEEE] border-t-black rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackPopup;
